import { Injectable } from '@angular/core';
import { Observable, of, map, catchError } from 'rxjs';
import { GoogleSheetsBaseService } from './google-sheets-base.service';

@Injectable({
  providedIn: 'root'
})
export class PartySheetsService extends GoogleSheetsBaseService {
  
  /**
   * Gets a list of party function labels from the 'person' sheet.
   * @returns An observable of the function labels.
   */
  getPersonFunctionLabels(): Observable<any> {
    const tabName = 'person';
    const startCell = 'D2';
    const endCell = 'D30';
    return this.getSheetData(tabName, startCell, endCell).pipe(
      map(response => response || [])
    );
  }

  /**
   * Gets party standing from the 'party' sheet.
   * @returns An observable of the party standing list.
   */
  getPartyStanding(): Observable<any> {
    const tabName = 'party';
    const startCell = 'B2';
    const endCell = 'B100';
    return this.getSheetColumnData(tabName, startCell, endCell).pipe(
      map(response => response || [])
    );
  }

  /**
   * Gets party types from the 'party' sheet.
   * @returns An observable of the party types.
   */
  getPartyTypes(): Observable<any> {
    const tabName = 'party';
    const startCell = 'A2';
    const endCell = 'A100';
    return this.getSheetColumnData(tabName, startCell, endCell).pipe(
      map(response => response || [])
    );
  }

  /**
   * Fetches party specialties from a Google Sheet.
   * @returns An observable that emits an array of specialty names.
   */
  public getPartySpecialties(): Observable<any> {
    const tabName = 'party';
    const startCell = 'C2';
    const endCell = 'C250';
    return this.getSheetColumnData(tabName, startCell, endCell).pipe(
      map(response => response || [])
    );
  }

  /**
   * Gets diversity standing options from the 'party' sheet.
   * @returns An observable of the diversity standing list.
   */
  getDiversityStanding(): Observable<string[]> {
    const tabName = 'party';
    const startCell = 'D2';
    const endCell = 'D50';
    return this.getSheetColumnData(tabName, startCell, endCell).pipe(
      map(response => response || [])
    );
  }

  /**
   * Gets license types from the 'party' sheet.
   * @returns An observable of the license types.
   */
  getLicenseTypes(): Observable<string[]> {
    const tabName = 'party';
    const startCell = 'E2';
    const endCell = 'E100';
    return this.getSheetColumnData(tabName, startCell, endCell).pipe(
      map(response => response || [])
    );
  }

  /**
   * Gets 1099 categories from the 'party' sheet.
   * @returns An observable of the 1099 categories.
   */
  get1099Categories(): Observable<string[]> {
    const tabName = 'party';
    const startCell = 'F2';
    const endCell = 'F50';
    return this.getSheetColumnData(tabName, startCell, endCell).pipe(
      map(response => response || [])
    );
  }

  /**
   * Gets business structure options from the 'party' sheet.
   * @returns An observable of the business structure options.
   */
  getBusinessStructures(): Observable<string[]> {
    const tabName = 'party';
    const startCell = 'G2';
    const endCell = 'G50';
    return this.getSheetColumnData(tabName, startCell, endCell).pipe(
      map(response => response || [])
    );
  }

  /**
   * Gets payment terms options from the 'party' sheet.
   * @returns An observable of the payment terms options.
   */
  getPaymentTerms(): Observable<string[]> {
    const tabName = 'party';
    const startCell = 'H2';
    const endCell = 'H50';
    return this.getSheetColumnData(tabName, startCell, endCell).pipe(
      map(response => response || [])
    );
  }
}